// Read-only client for marketing plans.
//
// This matches liquidretail_backend PR #662. Adjust this file if that
// contract changes. Callers should not parse the wire JSON themselves.
//
// GET /api/marketing/plans?brandId=
//   { plans: [{ planId, chainId, title, status, revision, origin,
//      weeklyBudget, updatedAt,
//      acceptedRevision: { planId, revision, acceptedAt, acceptedBy } | null }],
//     currentPlanId, acceptedPlanId }
//   currentPlanId is the latest revision. acceptedPlanId is the accepted
//   revision, which can be an older plan. Both are absent until that
//   list contract is deployed; the plans array still reads either way.
//
// GET /api/marketing/plans/:planId?brandId=&revision=
//   { summary: { planId, revision, status, title, origin, weeklyTotal,
//       currency, phase2Budget, margin,
//       lines: { reuse, create, unbound }, sections, markdown, text },
//     sections: { lines, budget, tests, targeting, creatives, copy, caveats },
//     history: [{ planId, revision, status, createdAt, updatedAt, acceptedAt,
//       acceptedBy, rejectedAt, rejectedReason, supersedes }],  // ascending
//     campaigns: { reused: [Link], created: [Link & { campaignId|null, status }] } }
//   Link: { campaignId, externalId, label, testId, testName, side, localKey }
//
// Section bodies read from those keys:
//   lines: { reuse, create, unbound } arrays of link-like rows, or counts
//   tests / budget.tests: { name|title|label, weeklyBudget|budget|amount }
//   targeting: string, string[], or { summary|text|note, audiences, geos, placements, age }
//   creatives / copy: string or { status, note|summary|text }
//   caveats: string or string[]
//   summary.markdown is the readable summary. summary.text is shown only
//   when markdown is absent. Counts on summary.lines are "N reuse · N create · N unbound".
//
// GET /api/marketing-plans/:planId/performance?brandId=&revision=
//   Spec path GET /marketing-plans/:id/performance. Header, pacing,
//   tests, chart series, creatives, and colorways. Parsed in
//   marketingPlans/performance.ts. A 404, 405, or 501 is a missing
//   endpoint, not a crash. unavailableReason is displayed as written.
//
// GET /api/campaigns?brandId=&planId=
//   Same { campaigns: [{ id, externalId, ... }] } as the unfiltered list.
//   A row matches when its id equals a plan link's campaignId, or its
//   externalId equals a plan link's externalId. Ads-summary rows key the
//   same two values as campaignId and externalId.
//   400: bad plan id. 404: plan is outside the brand. Both are errors.
//   An empty campaigns array means the plan touches nothing.
//   405/501, and 404/405/501 on the plans routes, are a missing endpoint:
//   an empty list, not a crash.
//
// Account ids, tokens, and feed keys are never copied onto the view.
// Plan ids are used for requests and are not rendered.

import { apiJson, isApiError } from '../auth/apiFetch';

export type PlanSummaryView = {
  id: string;
  title: string;
  status: string | null;
  revision: string | null;
  origin: string | null;
  draftReason: string | null;
  weeklyBudget: string | null;
  updatedLabel: string | null;
  acceptedLabel: string | null;
};

export type PlanLineView = {
  label: string;
  detail: string | null;
};

export type StatusNote = {
  status: string | null;
  note: string | null;
};

export type RevisionView = {
  revision: string | null;
  status: string | null;
  at: string | null;
  acceptedAt: string | null;
  acceptedBy: string | null;
  rejectedAt: string | null;
  rejectedReason: string | null;
};

export type PlanCampaignLink = {
  campaignId: string | null;
  externalId: string | null;
  testName: string | null;
  side: string | null;
};

export type PlanDetailView = {
  id: string;
  title: string;
  status: string | null;
  revision: string | null;
  origin: string | null;
  weeklyBudget: string | null;
  phase2Budget: string | null;
  margin: string | null;
  updatedLabel: string | null;
  prose: string | null;
  lineCounts: string | null;
  tests: { label: string; budget: string | null }[];
  reuse: PlanLineView[];
  create: PlanLineView[];
  unbound: PlanLineView[];
  targeting: string[];
  creatives: StatusNote | null;
  copy: StatusNote | null;
  caveats: string[];
  history: RevisionView[];
  links: PlanCampaignLink[];
};

export type PlanListResult = {
  plans: PlanSummaryView[];
  currentPlanId: string | null;
  acceptedPlanId: string | null;
  error: string | null;
};

export type PlanDetailResult = {
  plan: PlanDetailView | null;
  missing: boolean;
  error: string | null;
};

export type PlanCampaignFilter = {
  keys: string[];
  error: string | null;
};

type Rec = Record<string, unknown>;

function isRec(value: unknown): value is Rec {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function str(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function num(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function records(value: unknown): Rec[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRec);
}

// A bare JWT is three base64url segments and nothing else. Shorter
// dotted words are not tokens. Surrounding whitespace still counts as bare.
const BARE_JWT = /^[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}$/;

export function isSecretText(text: string): boolean {
  if (/bearer\s+\S{8,}/i.test(text)) return true;
  if (/EAA[A-Za-z0-9]{10,}/.test(text)) return true;
  if (/access[_-]?token/i.test(text)) return true;
  if (/\b(secret|password|api[_-]?key)\b\s*[:=]\s*\S+/i.test(text)) return true;
  if (/feed[_-]?key\s*[:=]\s*\S+/i.test(text)) return true;
  if (/act_\d{4,}/.test(text)) return true;
  if (BARE_JWT.test(text.trim())) return true;
  return false;
}

function isOpaqueId(text: string): boolean {
  return /^[a-f0-9]{24}$/i.test(text);
}

export function safeText(value: unknown, limit = 600): string | null {
  const text = str(value);
  if (!text || text.length > limit || isSecretText(text) || isOpaqueId(text)) return null;
  return text;
}

function currencyCode(value: unknown): string | null {
  const text = str(value);
  if (!text || !/^[A-Za-z]{3}$/.test(text)) return null;
  return text.toUpperCase();
}

function formatPlain(value: number): string {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

export function formatMoney(value: number, currency: string | null): string {
  if (currency) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
    } catch {
      return `${formatPlain(value)} ${currency}`;
    }
  }
  return formatPlain(value);
}

export function formatStamp(value: unknown): string | null {
  const text = str(value);
  if (!text) return null;
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC'
  }).format(date);
}

function revisionText(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return safeText(value, 40);
}

function moneyField(value: unknown, currency: string | null): string | null {
  if (isRec(value)) {
    const nested = currencyCode(value.currency) ?? currency;
    const amount = num(value.amount ?? value.weekly ?? value.weeklyBudget ?? value.weeklyTotal ?? value.value);
    if (amount == null) return null;
    return formatMoney(amount, nested);
  }
  const amount = num(value);
  if (amount == null) return null;
  return formatMoney(amount, currency);
}

function marginText(value: unknown): string | null {
  const text = safeText(value, 40);
  if (text && num(text) == null) return text;
  const amount = num(value);
  if (amount == null) return null;
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(amount);
}

function planIdOf(node: Rec): string | null {
  const raw = node.planId ?? node.id;
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  const text = str(raw);
  if (!text || isSecretText(text) || /^act_\d{4,}$/.test(text) || text.length > 80) return null;
  return text;
}

function matchKey(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  const text = str(value);
  if (!text || isSecretText(text) || /^act_\d{4,}$/.test(text) || text.length > 120) return null;
  return text;
}

function stringList(value: unknown): string[] {
  if (value == null) return [];
  const items = Array.isArray(value) ? value : [value];
  const out: string[] = [];
  for (const item of items) {
    const text = isRec(item)
      ? safeText(item.text ?? item.message ?? item.note ?? item.label ?? item.name, 400)
      : safeText(item, 400);
    if (text && !out.includes(text)) out.push(text);
  }
  return out;
}

function readLine(value: unknown): PlanLineView | null {
  if (!isRec(value)) {
    const text = safeText(value, 160);
    return text ? { label: text, detail: null } : null;
  }
  const label = safeText(value.label ?? value.name ?? value.title, 160) ?? safeText(value.testName, 160);
  const testName = safeText(value.testName, 80);
  const side = safeText(value.side ?? value.arm, 80);
  const status = safeText(value.status, 40);
  const detail = [testName && testName !== label ? testName : null, side, status && status !== label ? status : null]
    .filter((part): part is string => Boolean(part))
    .join(' · ') || null;
  if (!label && !detail) return null;
  return { label: label ?? detail ?? 'Line', detail: label ? detail : null };
}

function lineArray(value: unknown): PlanLineView[] | null {
  if (!Array.isArray(value)) return null;
  return value.map(readLine).filter((line): line is PlanLineView => line != null);
}

function lineCounts(value: unknown): string | null {
  if (!isRec(value)) return null;
  const parts: string[] = [];
  for (const key of ['reuse', 'create', 'unbound'] as const) {
    const count = num(value[key]);
    if (count == null) continue;
    parts.push(`${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(count)} ${key}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

function readLineGroups(value: unknown): { reuse: PlanLineView[]; create: PlanLineView[]; unbound: PlanLineView[] } | null {
  if (!isRec(value)) return null;
  const reuse = lineArray(value.reuse);
  const create = lineArray(value.create);
  const unbound = lineArray(value.unbound);
  if (!reuse && !create && !unbound) return null;
  return { reuse: reuse ?? [], create: create ?? [], unbound: unbound ?? [] };
}

function readLink(value: unknown): PlanCampaignLink | null {
  if (!isRec(value)) return null;
  const campaignId = matchKey(value.campaignId);
  const externalId = matchKey(value.externalId);
  const testName = safeText(value.testName, 80);
  const side = safeText(value.side, 80);
  if (!campaignId && !externalId && !testName && !side) return null;
  return { campaignId, externalId, testName, side };
}

function readLinks(value: unknown): PlanCampaignLink[] {
  return records(value).map(readLink).filter((link): link is PlanCampaignLink => link != null);
}

function testRows(value: unknown): Rec[] {
  if (Array.isArray(value)) return records(value);
  if (!isRec(value)) return [];
  for (const key of ['tests', 'items', 'rows'] as const) {
    if (Array.isArray(value[key])) return records(value[key]);
  }
  return [];
}

function readTests(sections: Rec, currency: string | null): PlanDetailView['tests'] {
  const direct = testRows(sections.tests);
  const fromBudget = isRec(sections.budget) ? testRows(sections.budget.tests ?? sections.budget.items) : [];
  const rows = direct.length ? direct : fromBudget;
  const tests: PlanDetailView['tests'] = [];
  rows.forEach((row, index) => {
    const name = safeText(row.name ?? row.title ?? row.label, 160);
    const budget = moneyField(row.weeklyBudget ?? row.budget ?? row.amount ?? row.weeklyTotal, currencyCode(row.currency) ?? currency);
    if (!name && !budget) return;
    tests.push({ label: name ?? `Test ${index + 1}`, budget });
  });
  return tests;
}

function readTargeting(value: unknown): string[] {
  if (typeof value === 'string' || Array.isArray(value)) return stringList(value);
  if (!isRec(value)) return [];
  const out = stringList(value.summary ?? value.text ?? value.note ?? value.markdown);
  for (const key of ['audiences', 'geos', 'placements'] as const) {
    const bits = stringList(value[key]);
    if (bits.length) out.push(`${key[0].toUpperCase()}${key.slice(1)}: ${bits.join(', ')}`);
  }
  const age = safeText(value.age ?? value.ageRange, 40);
  if (age) out.push(`Age: ${age}`);
  return out;
}

function readStatusNote(value: unknown): StatusNote | null {
  if (typeof value === 'string') {
    const note = safeText(value, 600);
    return note ? { status: null, note } : null;
  }
  if (!isRec(value)) return null;
  const status = safeText(value.status ?? value.state, 40);
  const note = safeText(value.note ?? value.summary ?? value.text ?? value.detail ?? value.markdown, 600);
  if (!status && !note) return null;
  return { status, note };
}

function readHistory(value: unknown): RevisionView[] {
  return records(value).map((row) => ({
    revision: revisionText(row.revision),
    status: safeText(row.status, 40),
    at: formatStamp(row.updatedAt ?? row.createdAt),
    acceptedAt: formatStamp(row.acceptedAt),
    acceptedBy: safeText(row.acceptedBy, 80),
    rejectedAt: formatStamp(row.rejectedAt),
    rejectedReason: safeText(row.rejectedReason, 400)
  })).filter((row) => row.revision || row.status || row.at || row.acceptedAt || row.rejectedAt || row.rejectedReason);
}

function acceptedLabel(value: unknown): string | null {
  if (!isRec(value)) return null;
  const revision = revisionText(value.revision);
  const at = formatStamp(value.acceptedAt);
  const parts = [revision ? `Accepted revision ${revision}` : null, at].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

function readOrigin(node: Rec): { origin: string | null; draftReason: string | null } {
  const field = node.origin;
  if (isRec(field)) {
    return {
      origin: safeText(field.kind ?? field.source ?? field.type, 40),
      draftReason: publicText(field.reason ?? node.reason ?? node.draftReason, 240)
    };
  }
  return {
    origin: safeText(field, 40),
    draftReason: publicText(node.reason ?? node.draftReason, 240)
  };
}

export function publicText(value: unknown, limit: number): string | null {
  const text = safeText(value, limit);
  if (!text || /[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/.test(text)) return null;
  return text;
}

const EMPTY_PLAN_LIST = { plans: [], currentPlanId: null, acceptedPlanId: null };

/** Plans plus the list's current and accepted ids. Missing ids stay null. */
export function readPlanList(body: unknown): { plans: PlanSummaryView[]; currentPlanId: string | null; acceptedPlanId: string | null } {
  return {
    plans: readPlanSummaries(body),
    currentPlanId: isRec(body) ? listedPlanId(body.currentPlanId) : null,
    acceptedPlanId: isRec(body) ? listedPlanId(body.acceptedPlanId) : null
  };
}

function listedPlanId(value: unknown): string | null {
  if (isRec(value)) return planIdOf(value);
  return planIdOf({ planId: value });
}

export function readPlanSummaries(body: unknown): PlanSummaryView[] {
  if (!isRec(body) || !Array.isArray(body.plans)) return [];
  const plans: PlanSummaryView[] = [];
  records(body.plans).forEach((node) => {
    const id = planIdOf(node);
    if (!id) return;
    const origin = readOrigin(node);
    plans.push({
      id,
      title: publicText(node.title, 160) ?? 'Marketing plan',
      status: safeText(node.status, 40),
      revision: revisionText(node.revision),
      origin: origin.origin,
      draftReason: origin.draftReason,
      weeklyBudget: moneyField(node.weeklyBudget, currencyCode(node.currency)),
      updatedLabel: formatStamp(node.updatedAt),
      acceptedLabel: acceptedLabel(node.acceptedRevision)
    });
  });
  return plans;
}

export function readPlanDetail(body: unknown, fallbackId = 'plan'): PlanDetailView | null {
  if (!isRec(body)) return null;
  const summary = isRec(body.summary) ? body.summary : null;
  const sections = isRec(body.sections) ? body.sections : {};
  const history = readHistory(body.history);
  const campaigns = isRec(body.campaigns) ? body.campaigns : {};
  if (!summary && !isRec(body.sections) && history.length === 0 && !isRec(body.campaigns)) return null;

  const currency = currencyCode(summary?.currency);
  const grouped = readLineGroups(sections.lines);
  const reused = readLinks(campaigns.reused);
  const created = readLinks(campaigns.created);
  const reuse = grouped?.reuse ?? linesFrom(campaigns.reused);
  const create = grouped?.create ?? linesFrom(campaigns.created);
  const unbound = grouped?.unbound ?? [];
  const markdown = summary ? safeText(summary.markdown, 4000) : null;
  const prose = markdown ?? (summary ? safeText(summary.text, 4000) : null);
  const newest = history.length ? history[history.length - 1] : null;

  return {
    id: (summary && planIdOf(summary)) ?? fallbackId,
    title: (summary && publicText(summary.title, 160)) ?? 'Marketing plan',
    status: summary ? safeText(summary.status, 40) : null,
    revision: summary ? revisionText(summary.revision) : newest?.revision ?? null,
    origin: summary ? safeText(summary.origin, 40) : null,
    weeklyBudget: summary ? moneyField(summary.weeklyTotal, currency) : null,
    phase2Budget: summary ? moneyField(summary.phase2Budget, currency) : null,
    margin: summary ? marginText(summary.margin) : null,
    updatedLabel: newest?.at ?? null,
    prose,
    lineCounts: summary ? lineCounts(summary.lines) : null,
    tests: readTests(sections, currency),
    reuse,
    create,
    unbound,
    targeting: readTargeting(sections.targeting),
    creatives: readStatusNote(sections.creatives),
    copy: readStatusNote(sections.copy),
    caveats: stringList(sections.caveats),
    history,
    links: [...reused, ...created]
  };
}

function linesFrom(value: unknown): PlanLineView[] {
  return records(value).map(readLine).filter((line): line is PlanLineView => line != null);
}

export function isMissingEndpoint(err: unknown): boolean {
  return isApiError(err) && (err.status === 404 || err.status === 405 || err.status === 501);
}

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message && !isSecretText(err.message)) return err.message;
  return 'Could not load marketing plans.';
}

export function listPlansUrl(brandId: string): string {
  return `/api/marketing/plans?brandId=${encodeURIComponent(brandId)}`;
}

export function planUrl(brandId: string, planId: string, revision?: string | null): string {
  const params = new URLSearchParams({ brandId });
  if (revision) params.set('revision', revision);
  return `/api/marketing/plans/${encodeURIComponent(planId)}?${params}`;
}

export function campaignsForPlanUrl(brandId: string, planId: string): string {
  return `/api/campaigns?brandId=${encodeURIComponent(brandId)}&planId=${encodeURIComponent(planId)}`;
}

export async function listMarketingPlans(brandId: string): Promise<PlanListResult> {
  try {
    const body = await apiJson<unknown>(listPlansUrl(brandId), { cache: 'no-store' });
    return { ...readPlanList(body), error: null };
  } catch (err: unknown) {
    if (isMissingEndpoint(err)) return { ...EMPTY_PLAN_LIST, error: null };
    return { ...EMPTY_PLAN_LIST, error: errorMessage(err) };
  }
}

export async function getMarketingPlan(brandId: string, planId: string, revision?: string | null): Promise<PlanDetailResult> {
  try {
    const body = await apiJson<unknown>(planUrl(brandId, planId, revision), { cache: 'no-store' });
    const plan = readPlanDetail(body, planId);
    if (!plan) return { plan: null, missing: true, error: null };
    return { plan, missing: false, error: null };
  } catch (err: unknown) {
    if (isMissingEndpoint(err)) return { plan: null, missing: true, error: null };
    return { plan: null, missing: false, error: errorMessage(err) };
  }
}

/** Keys from GET /api/campaigns?planId=. 400 and 404 are errors; an empty list is not. */
export async function listCampaignsForPlan(brandId: string, planId: string): Promise<PlanCampaignFilter> {
  try {
    const body = await apiJson<unknown>(campaignsForPlanUrl(brandId, planId), { cache: 'no-store' });
    return { keys: campaignKeys(body), error: null };
  } catch (err: unknown) {
    if (isApiError(err) && (err.status === 400 || err.status === 404)) {
      return { keys: [], error: errorMessage(err) };
    }
    if (isApiError(err) && (err.status === 405 || err.status === 501)) return { keys: [], error: null };
    return { keys: [], error: errorMessage(err) };
  }
}

export function campaignKeys(body: unknown): string[] {
  if (!isRec(body) || !Array.isArray(body.campaigns)) return [];
  const keys: string[] = [];
  for (const row of body.campaigns) {
    if (!isRec(row)) continue;
    for (const key of [matchKey(row.id), matchKey(row.externalId)]) {
      if (key && !keys.includes(key)) keys.push(key);
    }
  }
  return keys;
}

export function rowMatchesPlan(
  row: { campaignId?: unknown; externalId?: unknown },
  keys: string[]
): boolean {
  const set = new Set(keys);
  const campaignId = matchKey(row.campaignId);
  const externalId = matchKey(row.externalId);
  return Boolean((campaignId && set.has(campaignId)) || (externalId && set.has(externalId)));
}

export function filterRowsForPlan<T extends { campaignId?: unknown; externalId?: unknown }>(rows: T[], keys: string[]): T[] {
  return rows.filter((row) => rowMatchesPlan(row, keys));
}

export function armLabels(links: PlanCampaignLink[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const link of links) {
    const label = [link.testName, link.side].filter(Boolean).join(' · ');
    if (!label) continue;
    for (const key of [link.campaignId, link.externalId]) {
      if (!key) continue;
      out[key] = out[key] && !out[key].split(', ').includes(label) ? `${out[key]}, ${label}` : (out[key] ?? label);
    }
  }
  return out;
}

export function campaignArmLabel(
  row: { campaignId?: unknown; externalId?: unknown },
  labels: Record<string, string> = {}
): string | null {
  for (const key of [matchKey(row.campaignId), matchKey(row.externalId)]) {
    if (key && labels[key]) return labels[key];
  }
  return null;
}

export type PlanCampaignStatus = 'all' | 'filtered' | 'loading' | 'error';

export {
  getPlanPerformance,
  planPerformanceHref,
  planSuggestionsEnabled,
  performanceUrl,
  readPlanPerformance
} from './performance';
export type { PlanPerformance, PlanPerformanceResult } from './performance';

export function resolvePlanCampaigns<T extends { campaignId?: unknown; externalId?: unknown }>(input: {
  planId: string;
  rows: T[];
  keys: string[] | null;
  error: string | null;
  ready: boolean;
}): { rows: T[]; status: PlanCampaignStatus } {
  if (!input.planId) return { rows: input.rows, status: 'all' };
  if (!input.ready) return { rows: [], status: 'loading' };
  if (input.error) return { rows: [], status: 'error' };
  return { rows: filterRowsForPlan(input.rows, input.keys ?? []), status: 'filtered' };
}
